import { useId, useState, type FormEvent } from 'react'
import { Alert, ApiError, Button, Field } from '@jmvilaca-corp/auth-ui'
import {
  canEditEvents,
  fieldErrors,
  useCalendarMembers,
  useCreateEvent,
  useDeleteEvent,
  useUpdateEvent,
  type CalendarEvent,
  type CalendarSummary,
  type EventField,
  type EventType,
  type SaveEvent,
} from '../lib/calendars'
import { sportCoricoSource, standardSource } from '../lib/connectors'
import { allDayToIso, dayKey, eventDays, formatSpan, localToIso, timeKey } from '../lib/dates'
import {
  choiceFromMinutes,
  choiceLabel,
  choiceToMinutes,
  defaultReminder,
  reminderError,
  useSaveReminder,
  type ReminderChoice,
} from '../lib/reminders'
import Modal from './Modal'
import ReminderField from './ReminderField'
import SportCoricoDetails from './SportCoricoDetails'
import Tabs, { type Tab } from './Tabs'

const inputClass =
  'mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100'
const labelClass = 'block text-sm font-medium text-neutral-800 dark:text-neutral-200'

/** Evenement existant a ouvrir, ou jour (`AAAA-MM-JJ`) sur lequel en creer un. */
export type EventDialogTarget = { event: CalendarEvent } | { day: string }

export default function EventDialog({
  target,
  calendars,
  eventTypes,
  onClose,
}: {
  target: EventDialogTarget
  calendars: CalendarSummary[]
  eventTypes: EventType[]
  onClose: () => void
}) {
  const existing = 'event' in target ? target.event : null
  const calendar = existing === null ? null : calendars.find((item) => item.calendarId === existing.calendarId)

  if (existing !== null && (calendar === undefined || calendar === null || !canEditEvents(calendar))) {
    return (
      <Modal title={existing.title} onClose={onClose}>
        <EventDetails event={existing} calendar={calendar ?? null} eventTypes={eventTypes} />
      </Modal>
    )
  }

  return (
    <EventForm
      existing={existing}
      day={'day' in target ? target.day : null}
      calendars={calendars.filter(canEditEvents)}
      eventTypes={eventTypes}
      onClose={onClose}
    />
  )
}

function EventDetails({
  event,
  calendar,
  eventTypes,
}: {
  event: CalendarEvent
  calendar: CalendarSummary | null
  eventTypes: EventType[]
}) {
  const type = eventTypes.find((item) => item.key === event.type)
  const rows: [string, string][] = [
    ['Quand', formatSpan(event)],
    ['Type', type?.label ?? event.type],
    ...(event.location === null ? [] : [['Lieu', event.location] as [string, string]]),
    ...(event.participation === 'expected'
      ? [['Convocation', 'Pas encore envoyee. Attendue : les dernieres etaient avec cette equipe.'] as [string, string]]
      : []),
    ...(type?.fields ?? [])
      .filter((field) => event.details[field.key] !== undefined)
      .map((field) => [field.label, displayValue(field, event.details[field.key]!)] as [string, string]),
  ]

  return (
    <div className="space-y-4">
      {calendar !== null && (
        <p className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: calendar.color }} aria-hidden="true" />
          {calendar.name} · {calendar.source === standardSource ? 'lecture seule' : 'calendrier lie, lecture seule'}
        </p>
      )}
      {event.canceledAt !== null && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800 dark:bg-red-950/40 dark:text-red-200">
          Evenement annule : il n'apparait plus dans son calendrier d'origine.
        </p>
      )}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="font-medium text-neutral-700 dark:text-neutral-300">{label}</dt>
            <dd className="break-words text-neutral-900 dark:text-neutral-100">{value}</dd>
          </div>
        ))}
      </dl>
      {event.description !== null && (
        <p className="whitespace-pre-wrap text-sm text-neutral-800 dark:text-neutral-200">{event.description}</p>
      )}
      {calendar?.source === standardSource && <OwnReminder event={event} />}
      {calendar?.source === sportCoricoSource && <SportCoricoDetails eventId={event.eventId} />}
    </div>
  )
}

/**
 * Le rappel de l'utilisateur sur un evenement qu'il ne peut pas modifier : il
 * est a lui, la lecture seule ne l'empeche pas de le regler.
 */
function OwnReminder({ event }: { event: CalendarEvent }) {
  const save = useSaveReminder()
  const firstDay = eventDays(event).first
  const [saved, setSaved] = useState(event.reminder)
  const [choice, setChoice] = useState<ReminderChoice>(() =>
    choiceFromMinutes(event.reminder, { isAllDay: event.isAllDay, firstDay }),
  )
  const [error, setError] = useState<string | null>(null)
  const [openedAt] = useState(() => Date.now())

  const minutes = choiceToMinutes(choice, firstDay)
  // Commence : plus rien a rappeler, sauf a montrer le rappel qu'il avait.
  const over = new Date(event.isAllDay ? event.endsAt : event.startsAt).getTime() <= openedAt
  if (over && saved === null) {
    return null
  }

  const onSave = () => {
    const refused = minutes === null ? null : reminderError(minutes, event, new Date())
    setError(refused)
    if (refused !== null) {
      return
    }

    save.mutate(
      { eventId: event.eventId, minutesBefore: minutes },
      {
        onSuccess: () => setSaved(minutes),
        onError: (failure) =>
          setError(
            failure instanceof ApiError
              ? (Object.values(fieldErrors(failure.payload))[0] ?? failure.message)
              : failure.message,
          ),
      },
    )
  }

  return (
    <div className="space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <ReminderField
        choice={choice}
        onChange={(next) => {
          setChoice(next)
          setError(null)
        }}
        isAllDay={event.isAllDay}
        firstDay={firstDay}
      />
      {error !== null && <FieldError>{error}</FieldError>}
      {minutes !== saved && (
        <Button type="button" size="sm" disabled={save.isPending} onClick={onSave}>
          {save.isPending ? 'Enregistrement...' : 'Enregistrer le rappel'}
        </Button>
      )}
    </div>
  )
}

function displayValue(field: EventField, value: string): string {
  if (field.kind === 'boolean') {
    return value === 'true' ? 'Oui' : 'Non'
  }

  return field.options.find((option) => option.value === value)?.label ?? value
}

function EventForm({
  existing,
  day,
  calendars,
  eventTypes,
  onClose,
}: {
  existing: CalendarEvent | null
  day: string | null
  calendars: CalendarSummary[]
  eventTypes: EventType[]
  onClose: () => void
}) {
  const create = useCreateEvent()
  const update = useUpdateEvent()
  const remove = useDeleteEvent()

  const initialDays = existing === null ? null : eventDays(existing)
  const firstDay = initialDays?.first ?? day ?? dayKey(new Date())

  const [calendarId, setCalendarId] = useState(existing?.calendarId ?? calendars[0]?.calendarId ?? '')
  const [typeKey, setTypeKey] = useState(existing?.type ?? eventTypes[0]?.key ?? 'other')
  const [title, setTitle] = useState(existing?.title ?? '')
  const [isAllDay, setIsAllDay] = useState(existing?.isAllDay ?? false)
  const [startDay, setStartDay] = useState(firstDay)
  const [endDay, setEndDay] = useState(initialDays?.last ?? firstDay)
  const [startTime, setStartTime] = useState(
    existing !== null && !existing.isAllDay ? timeKey(new Date(existing.startsAt)) : '09:00',
  )
  const [endTime, setEndTime] = useState(
    existing !== null && !existing.isAllDay ? timeKey(new Date(existing.endsAt)) : '10:00',
  )
  const [location, setLocation] = useState(existing?.location ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [details, setDetails] = useState<Record<string, string>>(existing?.details ?? {})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  // Prevenir d'un nouvel evenement : propose seulement si d'autres personnes voient le calendrier.
  const people = useCalendarMembers(existing === null ? calendarId : '').data ?? []
  const [notify, setNotify] = useState(false)
  const [audience, setAudience] = useState<'everyone' | 'some'>('everyone')
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set())
  const formId = useId()
  const [section, setSection] = useState('type')

  // Le rappel de l'utilisateur. Un nouvel evenement en propose un d'office,
  // et dans un calendrier partage le pose aussi pour les autres personnes.
  const saveReminder = useSaveReminder()
  const [reminder, setReminder] = useState<ReminderChoice>(() =>
    existing === null
      ? defaultReminder(false)
      : choiceFromMinutes(existing.reminder, { isAllDay: existing.isAllDay, firstDay }),
  )
  const [reminderChosen, setReminderChosen] = useState(false)
  const [remindEveryone, setRemindEveryone] = useState(true)

  const type = eventTypes.find((item) => item.key === typeKey)
  const busy = create.isPending || update.isPending || remove.isPending || saveReminder.isPending

  // Un refus dans un onglet ferme resterait invisible : on l'ouvre.
  const showErrors = (byField: Record<string, string>) => {
    setErrors(byField)
    const keys = Object.keys(byField)
    const refused = [
      ...(keys.some((key) => key.startsWith('details.')) ? ['type'] : []),
      ...(keys.includes('description') ? ['description'] : []),
      ...(keys.includes('reminder') ? ['reminder'] : []),
      ...(keys.includes('notify') ? ['notify'] : []),
    ]
    if (refused.length > 0 && !refused.includes(section)) {
      setSection(refused[0]!)
    }
  }

  const onFailure = (error: Error) => {
    const { minutesBefore, ...others } = error instanceof ApiError ? fieldErrors(error.payload) : {}
    // Le rappel regle a part repond sous le nom de son propre champ.
    const byField: Record<string, string> = minutesBefore === undefined ? others : { ...others, reminder: minutesBefore }
    showErrors(byField)
    setFailure(Object.keys(byField).length > 0 ? null : error.message)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setFailure(null)

    const local: Record<string, string> = {}
    if (title.trim().length === 0) {
      local.title = 'Le titre est obligatoire.'
    }
    if (endDay < startDay || (!isAllDay && endDay === startDay && endTime < startTime)) {
      local.endsAt = 'La fin ne peut pas preceder le debut.'
    }
    if (calendarId === '') {
      local.calendar = 'Choisissez un calendrier.'
    }
    const notifying = existing === null && notify && people.length > 0
    if (notifying && audience === 'some' && chosen.size === 0) {
      local.notify = 'Choisissez au moins une personne.'
    }

    const span = isAllDay
      ? allDayToIso(startDay, endDay)
      : { startsAt: localToIso(startDay, startTime), endsAt: localToIso(endDay, endTime) }

    let minutes = choiceToMinutes(reminder, startDay)
    // Inchange, il ne repart pas : s'il a deja sonne, le serveur le refuserait comme passe.
    const reminderKept = existing !== null && minutes === existing.reminder
    const refusedReminder =
      minutes === null || reminderKept ? null : reminderError(minutes, { startsAt: span.startsAt, isAllDay }, new Date())
    if (refusedReminder !== null) {
      if (existing === null && !reminderChosen) {
        // Propose d'office, il ne vaut plus pour un evenement si proche : personne ne l'a demande.
        minutes = null
      } else {
        local.reminder = refusedReminder
      }
    }

    showErrors(local)
    if (Object.keys(local).length > 0) {
      return
    }

    const save: SaveEvent = {
      type: typeKey,
      title: title.trim(),
      ...span,
      isAllDay,
      location: location.trim(),
      description: description.trim(),
      // Seuls les champs du type courant partent : ceux d'un type quitte en
      // cours de saisie ne le concernent plus.
      details: Object.fromEntries(
        (type?.fields ?? [])
          .map((field) => [field.key, details[field.key] ?? ''] as const)
          .filter(([, value]) => value.trim().length > 0),
      ),
      ...(notifying ? { notify: { everyone: audience === 'everyone', userIds: audience === 'everyone' ? [] : [...chosen] } } : {}),
      ...(existing === null && minutes !== null
        ? { reminder: { minutesBefore: minutes, everyone: people.length > 0 && remindEveryone } }
        : {}),
    }

    if (existing === null) {
      create.mutate({ calendarId, event: save }, { onSuccess: onClose, onError: onFailure })
      return
    }

    // Le rappel n'appartient pas a l'evenement : il se regle a part, une fois
    // l'evenement enregistre, contre sa nouvelle heure.
    update.mutate(
      { eventId: existing.eventId, event: save },
      {
        onSuccess: () => {
          if (reminderKept) {
            onClose()
          } else {
            saveReminder.mutate(
              { eventId: existing.eventId, minutesBefore: minutes },
              { onSuccess: onClose, onError: onFailure },
            )
          }
        },
        onError: onFailure,
      },
    )
  }

  const sections: Tab[] = []

  if (type !== undefined && type.fields.length > 0) {
    sections.push({
      key: 'type',
      label: type.label,
      content: (
        <div className="space-y-4">
          {type.fields.map((field) => (
            <div key={field.key}>
              <TypeField
                field={field}
                value={details[field.key] ?? ''}
                onChange={(value) => setDetails((previous) => ({ ...previous, [field.key]: value }))}
              />
              {errors[`details.${field.key}`] !== undefined && <FieldError>{errors[`details.${field.key}`]!}</FieldError>}
            </div>
          ))}
        </div>
      ),
    })
  }

  sections.push({
    key: 'description',
    label: 'Description',
    content: (
      <>
        <textarea
          aria-label="Description"
          value={description}
          maxLength={2000}
          rows={4}
          onChange={(event) => setDescription(event.target.value)}
          className={inputClass}
        />
        {errors.description !== undefined && <FieldError>{errors.description}</FieldError>}
      </>
    ),
  })

  const reminderLabel = choiceLabel(reminder, startDay)
  sections.push({
    key: 'reminder',
    label: 'Rappel',
    ...(reminderLabel === null ? {} : { count: reminderLabel }),
    content: (
      <div className="space-y-3">
        <ReminderField
          choice={reminder}
          onChange={(next) => {
            setReminder(next)
            setReminderChosen(true)
          }}
          isAllDay={isAllDay}
          firstDay={startDay}
        />
        {errors.reminder !== undefined && <FieldError>{errors.reminder}</FieldError>}

        {existing === null && people.length > 0 && reminder.kind !== 'none' && (
          <>
            <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-200">
              <input
                type="checkbox"
                checked={remindEveryone}
                onChange={(event) => setRemindEveryone(event.target.checked)}
              />
              Aussi pour les autres personnes du calendrier ({people.length})
            </label>
            <p className="text-xs text-neutral-500">Chacune pourra ensuite regler ou retirer le sien.</p>
          </>
        )}
        {existing !== null && (
          <p className="text-xs text-neutral-500">Ce rappel est le votre : chaque personne du calendrier regle le sien.</p>
        )}
      </div>
    ),
  })

  if (existing === null && people.length > 0) {
    sections.push({
      key: 'notify',
      label: 'Notification',
      content: (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-200">
            <input type="checkbox" checked={notify} onChange={(event) => setNotify(event.target.checked)} />
            Prevenir les personnes de ce calendrier
          </label>

          {notify && (
            <>
              <label className="block">
                <span className={labelClass}>Qui prevenir</span>
                <select
                  value={audience}
                  onChange={(event) => setAudience(event.target.value === 'some' ? 'some' : 'everyone')}
                  className={inputClass}
                >
                  <option value="everyone">Tout le monde ({people.length})</option>
                  <option value="some">Certaines personnes</option>
                </select>
              </label>

              {audience === 'some' && (
                <ul className="space-y-1.5">
                  {people.map((person) => (
                    <li key={person.userId}>
                      <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-200">
                        <input
                          type="checkbox"
                          checked={chosen.has(person.userId)}
                          onChange={() =>
                            setChosen((current) => {
                              const next = new Set(current)
                              if (!next.delete(person.userId)) {
                                next.add(person.userId)
                              }
                              return next
                            })
                          }
                        />
                        {person.displayName}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {errors.notify !== undefined && <FieldError>{errors.notify}</FieldError>}

              <p className="text-xs text-neutral-500">
                Seules les personnes qui ont active les notifications sur un de leurs appareils la recevront.
              </p>
            </>
          )}
        </div>
      ),
    })
  }

  // Dans le pied de la fenetre, hors du formulaire : `form` les y rattache.
  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        {existing !== null &&
          (confirmingDelete ? (
            <span className="flex items-center gap-2">
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={busy}
                onClick={() => remove.mutate(existing.eventId, { onSuccess: onClose, onError: onFailure })}
              >
                Confirmer la suppression
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                Annuler
              </Button>
            </span>
          ) : (
            <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => setConfirmingDelete(true)}>
              Supprimer
            </Button>
          ))}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Fermer
        </Button>
        <Button type="submit" form={formId} size="sm" disabled={busy}>
          {create.isPending || update.isPending ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  )

  return (
    <Modal title={existing === null ? 'Nouvel evenement' : "Modifier l'evenement"} onClose={onClose} footer={footer}>
      <form id={formId} onSubmit={onSubmit} noValidate className="space-y-4">
        {failure !== null && <Alert>{failure}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={labelClass}>Calendrier</span>
            <select
              value={calendarId}
              disabled={existing !== null}
              onChange={(event) => {
                setCalendarId(event.target.value)
                // Les personnes ne sont pas les memes d'un calendrier a l'autre.
                setChosen(new Set())
              }}
              className={`${inputClass} disabled:opacity-60`}
            >
              {calendars.map((calendar) => (
                <option key={calendar.calendarId} value={calendar.calendarId}>
                  {calendar.name}
                </option>
              ))}
            </select>
            {errors.calendar !== undefined && <FieldError>{errors.calendar}</FieldError>}
          </label>

          <label className="block">
            <span className={labelClass}>Type</span>
            <select
              value={typeKey}
              onChange={(event) => {
                setTypeKey(event.target.value)
                // Les champs du type choisi passent devant, s'il en a.
                if ((eventTypes.find((item) => item.key === event.target.value)?.fields.length ?? 0) > 0) {
                  setSection('type')
                }
              }}
              className={inputClass}
            >
              {type === undefined && <option value={typeKey}>{typeKey}</option>}
              {eventTypes.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.type !== undefined && <FieldError>{errors.type}</FieldError>}
          </label>
        </div>

        <div>
          <Field label="Titre" value={title} maxLength={120} autoFocus onChange={(event) => setTitle(event.target.value)} />
          {errors.title !== undefined && <FieldError>{errors.title}</FieldError>}
        </div>

        <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-200">
          <input
            type="checkbox"
            checked={isAllDay}
            onChange={(event) => {
              setIsAllDay(event.target.checked)
              // Un delai en minutes n'a pas de sens pour une journee, ni l'inverse.
              if (reminder.kind !== 'none') {
                setReminder(defaultReminder(event.target.checked))
              }
            }}
          />
          Toute la journee
        </label>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Field
                label="Debut"
                type="date"
                value={startDay}
                onChange={(event) => {
                  setStartDay(event.target.value)
                  if (endDay < event.target.value) {
                    setEndDay(event.target.value)
                  }
                }}
              />
            </div>
            {!isAllDay && (
              <div className="w-36">
                <Field
                  label="Heure de debut"
                  type="time"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Field label="Fin" type="date" value={endDay} min={startDay} onChange={(event) => setEndDay(event.target.value)} />
            </div>
            {!isAllDay && (
              <div className="w-36">
                <Field label="Heure de fin" type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
              </div>
            )}
          </div>
        </div>
        {(errors.startsAt ?? errors.endsAt) !== undefined && <FieldError>{(errors.startsAt ?? errors.endsAt)!}</FieldError>}

        <div>
          <Field label="Lieu" value={location} maxLength={200} onChange={(event) => setLocation(event.target.value)} />
          {errors.location !== undefined && <FieldError>{errors.location}</FieldError>}
        </div>

        <Tabs label="Complements de l'evenement" tabs={sections} selected={section} onSelect={setSection} />
      </form>
    </Modal>
  )
}

function TypeField({
  field,
  value,
  onChange,
}: {
  field: EventField
  value: string
  onChange: (value: string) => void
}) {
  const label = field.required ? `${field.label} *` : field.label

  if (field.kind === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm text-neutral-800 dark:text-neutral-200">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(event) => onChange(event.target.checked ? 'true' : '')}
        />
        {label}
      </label>
    )
  }

  if (field.kind === 'choice') {
    return (
      <label className="block">
        <span className={labelClass}>{label}</span>
        <select value={value} onChange={(event) => onChange(event.target.value)} className={inputClass}>
          <option value="">—</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    )
  }

  return (
    <Field
      label={label}
      value={value}
      maxLength={200}
      {...(field.kind === 'number' ? { inputMode: 'decimal' as const } : {})}
      {...(field.kind === 'url' ? { type: 'url', placeholder: 'https://' } : {})}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

function FieldError({ children }: { children: string }) {
  return <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{children}</p>
}
